import { useMemo } from "react";
import styles from "../aic.module.css";
import { classMerge } from "../utility/classMerge";
import { getBaseId } from "./functions/getBaseId";
import {
  ADD_ROW_SPAN,
  AitHeaderCellData,
  AitLocationHeaderCell,
  REMOVE_ROW_SPAN,
} from "./functions/interface";
import { trCanAddRowSpan } from "./functions/trAddRowSpan";
import { trGetCellFromLocation } from "./functions/trGetCellFromLocation";
import { trCanRemoveRowSpan } from "./functions/trRemoveRowSpan";
import { useTableContext } from "./TableContextProvider";

interface AitCellRowSpansProps {
  location: AitLocationHeaderCell;
}

/**
 * Controls for adding or removing a header cell's row span.
 *
 * @template T - Cell payload type
 * @param props - Component props
 * @param props.location - The header cell location
 */
export const AitCellRowSpans = <T,>({ location }: AitCellRowSpansProps) => {
  const { state, dispatch } = useTableContext<T>();
  const originalHeaderData = state?.originalTableData.headerData;
  const thisCell: AitHeaderCellData<T> | undefined = state?.processedTableData
    ? trGetCellFromLocation(state.processedTableData, location)
    : undefined;
  const id = getBaseId(location, state?.id);

  const changeRowSpan = useMemo(() => {
    if (originalHeaderData && thisCell) {
      const canAdd = trCanAddRowSpan(originalHeaderData, location, thisCell.rowSpan + 1);
      const canRemove = trCanRemoveRowSpan(originalHeaderData, location, thisCell.rowSpan - 1);
      return {
        canAdd: canAdd.canAdd,
        canRemove: canRemove.canRemove,
      };
    }
    return { canAdd: false, canRemove: false };
  }, [originalHeaderData, thisCell, location]);

  return !thisCell ? null : (
    <div className={styles.aiwBodyRow}>
      <div className={styles.aioLabel}>Row span: </div>
      <div className={styles.aioRoValue}>{thisCell.repeatRowSpan ?? thisCell.rowSpan}</div>
      <div className={classMerge([styles.aioxButtonHolder, styles.padding2])}>
        {changeRowSpan.canAdd ? (
          <div
            id={`${id}-add-rowspan`}
            className={classMerge([styles.aioxButton, styles.aioxPlus])}
            title="Add row span"
            aria-label="Add row span"
            onClick={() =>
              dispatch({ operation: ADD_ROW_SPAN, location, newSpan: thisCell.rowSpan + 1 })
            }
          />
        ) : (
          <div className={styles.aioxButton} />
        )}
        {changeRowSpan.canRemove && (
          <div
            id={`${id}-remove-rowspan`}
            className={classMerge([styles.aioxButton, styles.aioxMinus])}
            title="Remove row span"
            aria-label="Remove row span"
            onClick={() =>
              dispatch({ operation: REMOVE_ROW_SPAN, location, newSpan: thisCell.rowSpan - 1 })
            }
          />
        )}
      </div>
    </div>
  );
};

AitCellRowSpans.displayName = "AitCellRowSpans";
